/**
 * CR116: Deal Finder Preview Component
 * CR124: Added support for custom title/subtitle and Asset Studio logos
 * CR126: Added header typography preset support
 * CR130: Added DealCardUXProvider for card size/density configuration
 * 
 * Renders a live preview of the Deal Finder page based on draft layout configuration.
 * Shows placeholder versions of all page blocks with real-time updates.
 */

import { useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Monitor,
  Smartphone,
  Search,
  Filter,
  ArrowUpDown,
  LayoutGrid,
  List,
  Star,
  Eye,
  ChevronUp,
  RefreshCw,
} from "lucide-react";
import type { LayoutJson, HeaderBlockConfig, ResultsBlockConfig } from "@/lib/layout-studio/types";
import { SORT_LABELS, DEFAULT_HEADER_CONFIG, DEFAULT_RESULTS_CONFIG } from "@/lib/layout-studio/types";
import { resolveFontFamily, DEFAULT_BODY_FONT_ID, DEFAULT_HEADING_FONT_ID } from "@/lib/theme/fontRegistry";
import { resolveHeaderTypography } from "@/lib/layout-studio/headerTypographyPresets";
import { DealGiftCard } from "@/components/deals/DealGiftCard";
import { DealCardWithDetails } from "@/components/deals/DealCardWithDetails";
import { DealDetailsPanel } from "@/components/deals/DealDetailsPanel";
import { DealDetailsProvider, useDealDetails } from "@/contexts/DealDetailsContext";
import type { Deal, DealCategory } from "@/lib/types/deals";
import { PreviewErrorBoundary } from "@/components/admin/preview";
import { useAppAssets } from "@/hooks/useAppAssets";
import { sanitizeSvg } from "@/lib/asset-studio/types";
// CR130: Import card UX provider for size/density
import { DealCardUXProvider, isDevEnvironment } from "@/contexts/DealCardUXContext";
import { DEFAULT_DEAL_CARD_UX_CONFIG, CARD_SIZE_LABELS, DENSITY_LABELS, DETAILS_MODE_LABELS } from "@/lib/layout-studio/dealCardUXTypes";

// ================================================================
// MOCK DATA - CR130: Visual test set with diverse content lengths
// ================================================================

interface MockDeal extends Partial<Deal> {
  _previewId: string;
  /** Label for visual testing */
  _testLabel?: string;
}

/**
 * CR130: Visual test set with varied content lengths to validate clamping/spacing:
 * - Short vs long merchant names
 * - Short vs long headlines
 * - With/without fine print
 * - With/without badges
 * - Different deal types
 */
const MOCK_DEALS: MockDeal[] = [
  // Short merchant, short headline (baseline)
  {
    _previewId: "test-short-short",
    _testLabel: "Short/Short",
    merchant: "Walmart",
    promo_merchant_name: "Walmart",
    reward_brand_name: "Walmart",
    headline_mechanic: "10% off all gift cards",
    deal_category: "DISCOUNT" as DealCategory,
    deal_type: "discount",
    discount_percent: 10,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 10,
    source_type: "website",
    purchase_gift_card_brand: "Walmart",
    gift_card_brand: "Walmart",
  },
  // Long merchant name, long headline (stress test clamping)
  {
    _previewId: "test-long-long",
    _testLabel: "Long/Long",
    merchant: "Shoppers Drug Mart Beauty Boutique",
    promo_merchant_name: "Shoppers Drug Mart Beauty Boutique",
    reward_brand_name: "The Keg Steakhouse & Bar",
    headline_mechanic: "Buy $100 in The Keg gift cards and receive a $25 bonus card plus 20x PC Optimum points on your next purchase",
    deal_category: "BONUS" as DealCategory,
    deal_type: "bonus",
    bonus_amount: 25,
    spend_amount: 100,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 25,
    source_type: "flyer",
    purchase_gift_card_brand: "The Keg",
    gift_card_brand: "The Keg",
    bonus_merchant: "Shoppers",
    fine_print: "Limit 2 per customer. Cannot be combined with other offers. Valid in-store only at participating locations.",
  },
  // Short merchant, with badges (test badge wrap)
  {
    _previewId: "test-badges",
    _testLabel: "With Badges",
    merchant: "Costco",
    promo_merchant_name: "Costco",
    reward_brand_name: "Costco",
    headline_mechanic: "Members-only: 15% off",
    deal_category: "DISCOUNT" as DealCategory,
    deal_type: "discount",
    discount_percent: 15,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 15,
    source_type: "website",
    purchase_gift_card_brand: "Costco",
    gift_card_brand: "Costco",
  },
  // Points deal with long program name
  {
    _previewId: "test-points",
    _testLabel: "Points Deal",
    merchant: "Loblaws",
    promo_merchant_name: "Loblaws",
    reward_brand_name: "PC Optimum",
    headline_mechanic: "Earn 5,000 PC Optimum Points on $50 gift card purchase",
    deal_category: "POINTS" as DealCategory,
    deal_type: "points",
    points_amount: 5000,
    points_program_name: "PC Optimum",
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 10,
    source_type: "flyer",
    purchase_gift_card_brand: "Loblaws",
    gift_card_brand: "Loblaws",
  },
  // Unclassified (edge case)
  {
    _previewId: "test-unclassified",
    _testLabel: "Unclassified",
    merchant: "Unknown Store",
    promo_merchant_name: "Unknown Store",
    headline_mechanic: "Special offer - details vary",
    deal_category: "UNCLASSIFIED" as DealCategory,
    deal_type: "unclassified",
    start_date: new Date().toISOString(),
    source_type: "website" as const, // Using valid SourceType
    confidence: "Low",
  },
  // Long fine print (test overflow)
  {
    _previewId: "test-fine-print",
    _testLabel: "Long Fine Print",
    merchant: "Metro",
    promo_merchant_name: "Metro",
    reward_brand_name: "Boston Pizza",
    headline_mechanic: "Buy $50, get $10 bonus",
    deal_category: "BONUS" as DealCategory,
    deal_type: "bonus",
    bonus_amount: 10,
    spend_amount: 50,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
    effective_percent_back: 20,
    source_type: "flyer",
    purchase_gift_card_brand: "Boston Pizza",
    gift_card_brand: "Boston Pizza",
    fine_print: "This offer is valid from January 1 to February 28. Limit one bonus card per customer per transaction. Bonus cards can be redeemed starting March 1. Not valid with any other offer or discount. Void where prohibited. See store for details. Gift cards do not expire.",
  },
];

// ================================================================
// TYPES
// ================================================================

import type { PreviewDealData, PreviewDataSource } from "@/hooks/useLayoutPreviewDeals";

interface DealFinderPreviewProps {
  /** Draft layout configuration */
  layout: LayoutJson;
  /** Force re-render key when config changes */
  previewKey?: string | number;
  /** Optional callback when preview needs scroll reset */
  onScrollToTop?: () => void;
  /** CR220: Real deals data from preview source */
  deals?: PreviewDealData[];
  /** CR220: Loading state for deals */
  dealsLoading?: boolean;
  /** CR220: Current data source mode */
  dataSource?: PreviewDataSource;
}

// ================================================================
// INNER COMPONENT FOR RESULTS WITH DETAILS
// CR130 Phase 3: Renders the deal grid with details mode support
// ================================================================

interface ResultsGridInnerProps {
  isGrid: boolean;
  isCompact: boolean;
  resultsConfig: ResultsBlockConfig;
  gridCols: number;
  uiFontFamily: string;
  dealCardUX: typeof DEFAULT_DEAL_CARD_UX_CONFIG;
  deals: Array<{ _previewId?: string; merchant: string } & Partial<Deal>>;
  dealsLoading?: boolean;
  dataSource?: string;
}

function ResultsGridInner({ 
  isGrid, 
  isCompact, 
  resultsConfig, 
  gridCols, 
  uiFontFamily,
  dealCardUX,
  deals,
  dealsLoading,
  dataSource,
}: ResultsGridInnerProps) {
  const { selectedDeal, closePanel } = useDealDetails();
  
  // Use provided deals
  const displayDeals = deals.slice(0, resultsConfig.pageSize || 6);
  
  return (
    <div className="p-4" style={{ fontFamily: uiFontFamily }}>
      {/* CR220: Data source indicator badge - fixed to show correct source label */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-xs bg-muted/50">
          {CARD_SIZE_LABELS[dealCardUX.cardSize].label} • {DENSITY_LABELS[dealCardUX.density].label}
        </Badge>
        <Badge variant="outline" className="text-xs bg-primary/10 text-primary">
          {DETAILS_MODE_LABELS[dealCardUX.detailsMode].label} mode
        </Badge>
        {dataSource && (
          <Badge 
            variant={dataSource === "batch" ? "default" : dataSource === "live" ? "secondary" : "outline"} 
            className="text-xs"
          >
            {dataSource === "batch" ? "BATCH" : 
             dataSource === "staging" ? "STAGING" :
             dataSource === "live" ? "LIVE" :
             dataSource === "admin" ? "ADMIN" :
             dataSource === "mock" ? "MOCK" : 
             dataSource.toUpperCase()} data
          </Badge>
        )}
        {/* Show deal count for debugging */}
        <Badge variant="outline" className="text-xs text-muted-foreground">
          {displayDeals.length} deals
        </Badge>
      </div>
      
      {dealsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Skeleton className="h-32 w-full" />
        </div>
      ) : displayDeals.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          <p>No deals found</p>
          <p className="text-xs mt-1">Try a different data source or batch run</p>
          {dataSource === "batch" && (
            <p className="text-xs mt-1 text-amber-600">
              Note: The batch may not have any deduped deals yet
            </p>
          )}
        </div>
      ) : isGrid ? (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}
        >
          {displayDeals.map((deal, idx) => {
            // CR220: Log any deals that might fail to render
            const hasMinimumFields = deal.merchant && (deal.headline_mechanic || deal.deal_type);
            if (!hasMinimumFields) {
              console.warn("[CR220][CardRender] Deal may not render properly:", { 
                id: deal._previewId, 
                merchant: deal.merchant,
                deal_type: deal.deal_type,
                headline: deal.headline_mechanic?.slice(0, 30),
              });
            }
            
            return (
              <PreviewErrorBoundary key={deal._previewId || `deal-${idx}`} previewContext="DealFinderPreview">
                <div className={isCompact ? "scale-90 origin-top-left" : ""}>
                  <DealCardWithDetails 
                    deal={deal as Deal} 
                    dealId={deal._previewId || `deal-${idx}`} 
                    isAdmin={false} 
                  />
                </div>
              </PreviewErrorBoundary>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {displayDeals.map((deal, idx) => (
            <PreviewErrorBoundary key={deal._previewId || `deal-${idx}`} previewContext="DealFinderPreview">
              <div className={isCompact ? "scale-95" : ""}>
                <DealCardWithDetails 
                  deal={deal as Deal} 
                  dealId={deal._previewId || `deal-${idx}`} 
                  isAdmin={false} 
                />
              </div>
            </PreviewErrorBoundary>
          ))}
        </div>
      )}
      
      {/* Pagination indicator */}
      {resultsConfig.pagination === "pages" && (
        <div className="mt-4 flex justify-center gap-1">
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled>1</Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">2</Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">3</Button>
        </div>
      )}
      {resultsConfig.pagination === "infinite" && (
        <div className="mt-4 text-center text-xs text-muted-foreground">
          Scroll for more...
        </div>
      )}
      
      {/* Panel mode: render the side panel */}
      {dealCardUX.detailsMode === "panel" && (
        <DealDetailsPanel
          deal={selectedDeal}
          sectionsConfig={dealCardUX.detailsSections ?? []}
          isAdmin={false}
          isOpen={!!selectedDeal}
          onClose={closePanel}
          width={400}
        />
      )}
    </div>
  );
}

// ================================================================
// COMPONENT
// ================================================================

type ViewportSize = "desktop" | "mobile";

export function DealFinderPreview({ 
  layout, 
  previewKey, 
  onScrollToTop,
  deals: externalDeals,
  dealsLoading = false,
  dataSource = "mock",
}: DealFinderPreviewProps) {
  const [viewport, setViewport] = useState<ViewportSize>("desktop");
  const [mockFilter, setMockFilter] = useState("");
  const [mockSort, setMockSort] = useState<string>(layout.defaultSort);

  // CR124: Fetch assets for logo resolution
  const { assets: allAssets } = useAppAssets({ status: "active" });
  
  // CR124: Resolve logo asset by ID
  const resolveLogoAsset = useCallback((logoAssetId?: string) => {
    if (!logoAssetId) return null;
    return allAssets.find((a) => a.id === logoAssetId);
  }, [allAssets]);

  // CR130: Get card UX config from layout, with defaults
  const dealCardUX = layout.dealCardUX ?? DEFAULT_DEAL_CARD_UX_CONFIG;
  
  // CR220: Use external deals or fall back to internal mock data
  const effectiveDeals = useMemo(() => {
    if (externalDeals && externalDeals.length > 0) {
      return externalDeals as Array<{ _previewId?: string; merchant: string } & Partial<Deal>>;
    }
    // Fallback to MOCK_DEALS (internal) - cast to compatible type
    return MOCK_DEALS as Array<{ _previewId?: string; merchant: string } & Partial<Deal>>;
  }, [externalDeals]);
  
  // CR170 DEBUG: Log received config
  console.log('[CR170-Preview] DealFinderPreview received dealCardUX.detailsSections:', 
    dealCardUX.detailsSections?.map(s => `${s.key}:${s.enabled}`).join(', '));

  // Get typography settings with safe defaults
  const typography = layout.typography ?? {};
  const uiFontFamily = resolveFontFamily(typography.uiFontId, DEFAULT_BODY_FONT_ID);
  const titleFontFamily = resolveFontFamily(typography.titleFontId, DEFAULT_HEADING_FONT_ID);

  // Sort blocks by order
  const sortedBlocks = useMemo(() => {
    return [...(layout.blocks || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [layout.blocks]);

  // Get enabled filters sorted by order
  const enabledFilters = useMemo(() => {
    return [...(layout.filters || [])]
      .filter((f) => f.enabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [layout.filters]);

  // Get enabled sorts
  const enabledSorts = useMemo(() => {
    return [...(layout.sortOptions || [])]
      .filter((s) => s.enabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [layout.sortOptions]);

  // Results config with defaults
  const resultsConfig: ResultsBlockConfig = layout.resultsConfig || DEFAULT_RESULTS_CONFIG;

  // Grid columns based on viewport
  const gridCols = useMemo(() => {
    if (viewport === "mobile") return resultsConfig.gridColumns?.mobile ?? 1;
    return resultsConfig.gridColumns?.desktop ?? 3;
  }, [viewport, resultsConfig.gridColumns]);

  // Handle scroll to top
  const handleScrollToTop = useCallback(() => {
    onScrollToTop?.();
  }, [onScrollToTop]);

  // Render header block - CR124: uses custom title/subtitle/logo, CR126: uses typography presets
  const renderHeader = useCallback((config: HeaderBlockConfig) => {
    // CR124: resolve logo from Asset Studio or fallback
    const logoAsset = resolveLogoAsset(config.logoAssetId);
    const logoSvg = logoAsset ? sanitizeSvg(logoAsset.svg) : null;
    
    // CR124: use new fields, fall back to legacy overrides
    const titleText = config.titleText || config.titleOverride || "Deal Finder";
    const subtitleText = config.subtitleText || config.subtitleOverride || "Find the best gift card deals";
    
    // CR126: resolve typography preset styles
    const { titleStyle, subtitleStyle } = resolveHeaderTypography(
      config.headerTypography,
      null, // themeTokens - could be passed if needed
      typography // Layout Studio typography settings
    );
    
    const logoSizeClasses = {
      sm: "w-8 h-8",
      md: "w-12 h-12",
      lg: "w-16 h-16",
    };
    const logoIconSize = {
      sm: "h-4 w-4",
      md: "h-6 w-6",
      lg: "h-8 w-8",
    };
    
    return (
      <div
        className={`py-4 px-4 border-b bg-card ${
          config.alignment === "center" ? "text-center" : "text-left"
        }`}
      >
        <div className="flex items-center gap-3" style={{ justifyContent: config.alignment === "center" ? "center" : "flex-start" }}>
          {config.showLogo && (
            <div
              className={`rounded bg-primary/10 flex items-center justify-center ${logoSizeClasses[config.logoSize]}`}
            >
              {/* CR201: Use img instead of dangerouslySetInnerHTML */}
              {logoSvg ? (
                <img 
                  src={`data:image/svg+xml,${encodeURIComponent(logoSvg)}`}
                  alt="Site logo"
                  className={logoSizeClasses[config.logoSize]}
                />
              ) : (
                <LayoutGrid className={logoIconSize[config.logoSize]} />
              )}
            </div>
          )}
          {config.showTitle && (
            <div>
              <h1 style={titleStyle}>
                {titleText}
              </h1>
              {config.showSubtitle && (
                <p className="text-muted-foreground" style={subtitleStyle}>
                  {subtitleText}
                </p>
              )}
            </div>
          )}
        </div>
        {config.bannerText && (
          <div className="mt-2 py-1 px-3 bg-primary/10 rounded text-xs text-primary font-medium">
            {config.bannerText}
          </div>
        )}
      </div>
    );
  }, [typography, resolveLogoAsset]);

  // Render search and filters block
  const renderSearchFilters = useCallback(() => {
    return (
      <div className="py-3 px-4 border-b bg-muted/20" style={{ fontFamily: uiFontFamily }}>
        <div className="flex flex-wrap gap-2 items-center">
          {enabledFilters.map((filter) => {
            if (filter.id === "search") {
              return (
                <div key={filter.id} className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search deals..."
                    className="pl-8 h-8 text-sm"
                    value={mockFilter}
                    onChange={(e) => setMockFilter(e.target.value)}
                  />
                </div>
              );
            }
            if (filter.id === "clear_all") {
              return (
                <Button key={filter.id} variant="ghost" size="sm" className="text-xs">
                  Clear All
                </Button>
              );
            }
            return (
              <Select key={filter.id}>
                <SelectTrigger className="h-8 text-xs w-auto min-w-[100px]">
                  <SelectValue placeholder={filter.id.replace(/_/g, " ")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="option1">Option 1</SelectItem>
                  <SelectItem value="option2">Option 2</SelectItem>
                </SelectContent>
              </Select>
            );
          })}
        </div>
      </div>
    );
  }, [enabledFilters, mockFilter, uiFontFamily]);

  // Render sort bar block - CR220: Use actual deal count
  // Note: Use effectiveDeals directly (not .length) as dependency to ensure proper reactivity
  const renderSortBar = useCallback(() => {
    const dealCount = effectiveDeals.length;
    console.log('[CR220][SortBar] Rendering with dealCount=', dealCount, 'dealsLoading=', dealsLoading);
    return (
      <div className="py-2 px-4 border-b flex items-center justify-between" style={{ fontFamily: uiFontFamily }}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{dealsLoading ? "Loading..." : `${dealCount} deals found`}</span>
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
          <Select value={mockSort} onValueChange={setMockSort}>
            <SelectTrigger className="h-7 text-xs w-auto min-w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {enabledSorts.map((s) => (
                <SelectItem key={s.id} value={s.id} className="text-xs">
                  {SORT_LABELS[s.id]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }, [enabledSorts, mockSort, uiFontFamily, effectiveDeals, dealsLoading]);

  // Render results block - CR130 Phase 3: Now includes details mode support
  const renderResults = useCallback(() => {
    const isGrid = resultsConfig.layoutType === "grid";
    const isCompact = resultsConfig.density === "compact";

    return (
      <DealCardUXProvider 
        cardSize={dealCardUX.cardSize} 
        density={dealCardUX.density}
        detailsMode={dealCardUX.detailsMode}
        expandSingleOpen={dealCardUX.expandSingleOpen}
        detailsSections={dealCardUX.detailsSections}
        customHeightsEnabled={dealCardUX.customHeightsEnabled}
        customHeightsPx={dealCardUX.customHeightsPx}
        flyerInteraction={dealCardUX.flyerInteraction}
        feedbackWidget={dealCardUX.feedbackWidget}
      >
        <DealDetailsProvider
          mode={dealCardUX.detailsMode}
          singleExpandMode={dealCardUX.expandSingleOpen ?? true}
          sectionsConfig={dealCardUX.detailsSections ?? []}
        >
          <ResultsGridInner
            isGrid={isGrid}
            isCompact={isCompact}
            resultsConfig={resultsConfig}
            gridCols={gridCols}
            uiFontFamily={uiFontFamily}
            dealCardUX={dealCardUX}
            deals={effectiveDeals}
            dealsLoading={dealsLoading}
            dataSource={dataSource}
          />
        </DealDetailsProvider>
      </DealCardUXProvider>
    );
  }, [resultsConfig, gridCols, uiFontFamily, dealCardUX, effectiveDeals, dealsLoading, dataSource]);

  // Render featured deals block - CR130: Also wraps in DealCardUXProvider
  const renderFeaturedDeals = useCallback(() => {
    return (
      <DealCardUXProvider 
        cardSize={dealCardUX.cardSize} 
        density={dealCardUX.density}
        customHeightsEnabled={dealCardUX.customHeightsEnabled}
        customHeightsPx={dealCardUX.customHeightsPx}
        flyerInteraction={dealCardUX.flyerInteraction}
        feedbackWidget={dealCardUX.feedbackWidget}
      >
        <div className="py-4 px-4 border-b bg-accent/10" style={{ fontFamily: uiFontFamily }}>
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-accent-foreground" />
            <span className="font-medium text-sm" style={{ fontFamily: titleFontFamily }}>Featured Deals</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {MOCK_DEALS.slice(0, 2).map((deal) => (
              <div key={deal._previewId} className="flex-shrink-0 w-64">
                <PreviewErrorBoundary previewContext="DealFinderPreview-featured">
                  <DealGiftCard deal={deal as Deal} isAdmin={false} />
                </PreviewErrorBoundary>
              </div>
            ))}
          </div>
        </div>
      </DealCardUXProvider>
    );
  }, [titleFontFamily, uiFontFamily, dealCardUX]);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Live Preview</CardTitle>
            <Badge variant="secondary" className="text-[10px]">
              Previewing draft
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <ToggleGroup type="single" value={viewport} onValueChange={(v) => v && setViewport(v as ViewportSize)}>
              <ToggleGroupItem value="desktop" size="sm" className="h-7 w-7 p-0">
                <Monitor className="h-3.5 w-3.5" />
              </ToggleGroupItem>
              <ToggleGroupItem value="mobile" size="sm" className="h-7 w-7 p-0">
                <Smartphone className="h-3.5 w-3.5" />
              </ToggleGroupItem>
            </ToggleGroup>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleScrollToTop}>
              <ChevronUp className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Preview Container */}
        <div
          key={previewKey}
          className={`mx-auto bg-background border-x transition-all ${
            viewport === "mobile" ? "max-w-[375px]" : "max-w-full"
          }`}
          style={{ fontFamily: uiFontFamily }}
        >
          {sortedBlocks.map((block) => {
            if (!block.enabled) return null;

            switch (block.id) {
              case "header": {
                const headerConfig = (block.config as HeaderBlockConfig) || DEFAULT_HEADER_CONFIG;
                return <div key={block.id}>{renderHeader(headerConfig)}</div>;
              }

              case "search_filters":
                return <div key={block.id}>{renderSearchFilters()}</div>;

              case "sort_bar":
                return <div key={block.id}>{renderSortBar()}</div>;

              case "results":
                return <div key={block.id}>{renderResults()}</div>;

              case "featured_deals":
                return <div key={block.id}>{renderFeaturedDeals()}</div>;

              default:
                return null;
            }
          })}

          {/* Empty state if no blocks enabled */}
          {sortedBlocks.filter((b) => b.enabled).length === 0 && (
            <div className="py-16 text-center text-muted-foreground">
              <LayoutGrid className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No blocks enabled</p>
              <p className="text-sm">Enable blocks in the editor to see a preview</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
